import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  ListItemText,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Box,
  Chip,
  Typography,
  Button,
  SelectChangeEvent,
  ListSubheader,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import { getJoinedTablesFieldsSagaAction } from "../../../../../../../redux/saga/partner-details/saga-actions";
import { columnFilterLabelType, postJoinedTablesFieldsDataAPI, postJoinedTablesFieldsDataType } from "../../../../../../../redux/saga/partner-details";
import { partnerDetailsSliceAction } from "../../../../../../../redux/slices/partner-details";
import { SearchInput } from "@hid-galaxy-ui/galaxy-react";

type OutlineTabProps = {
  onApplyFilters: any;
  onResetFilters: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  selectedColumns: string[];
  setSelectedColumns: React.Dispatch<React.SetStateAction<string[]>>;
  groupColumns: string[];
  setGroupColumns: React.Dispatch<React.SetStateAction<string[]>>;
  groupRows: string[];
  setGroupRows: React.Dispatch<React.SetStateAction<string[]>>;
};

const OutlineTab: React.FC<OutlineTabProps> = ({
  onApplyFilters,
  onResetFilters,
  disabled = false,
  readOnly = false,
  selectedColumns,
  setSelectedColumns,
  groupColumns,
  setGroupColumns,
  groupRows,
  setGroupRows,
}) => {
  const [reportTitle, setReportTitle] = useState("");
  const [columnSearch, setColumnSearch] = useState("");

  const { joinedTablesFields, reportFilter }: { joinedTablesFields: columnFilterLabelType[], reportFilter: postJoinedTablesFieldsDataType } = useAppSelector((state) => state.partner_details);
  const dispatch = useAppDispatch();
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  // API call to get the columns
  useEffect(() => {
    if (!joinedTablesFields || joinedTablesFields.length === 0) {
      dispatch(getJoinedTablesFieldsSagaAction());
    }
  }, [dispatch]);

  const getColumnLabel = (value: string) => {
    return (
      joinedTablesFields.find((col) => col.value === value)?.label || value
    );
  };

  useEffect(() => {
    if (!isEditingTitle && reportFilter?.report_title !== undefined) {
      setReportTitle(reportFilter.report_title);
    }
  }, [reportFilter?.report_title, isEditingTitle]);

  const QUICK_SELECT_LIMIT = 4;
  const QUICK_SELECT_COLUMNS = useMemo(() => {
    if (!joinedTablesFields.length) return [];

    const shuffled = [...joinedTablesFields].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, QUICK_SELECT_LIMIT);
  }, [joinedTablesFields]);

  const handleSelectChange =
  (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
  (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    const newValue = typeof value === "string" ? value.split(",") : value;

    // Update local state
    setter(newValue);

    // Update Redux immediately so table uses correct columns
    dispatch(partnerDetailsSliceAction.setReportFilterColumnAction(newValue));
  };

  const handleRemoveChip = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter((prev) => {
      const updated = prev.filter((item) => item !== value);

      // Update Redux to avoid table reset
      dispatch(partnerDetailsSliceAction.setReportFilterColumnAction(updated));

      return updated;
    });
  };

  const handleApplyClick = () => {
    if (!reportTitle.trim() && selectedColumns.length === 0) return;

    const title = reportTitle.trim() || "Untitled";

    setIsEditingTitle(false);
    
    onApplyFilters({
      title,
      groupColumns: groupColumns ? [groupColumns] : [],
      groupRows: groupRows ? [groupRows] : [],
      selectedColumns,
    });

    dispatch(partnerDetailsSliceAction.setReportFilterTitleAction(title));
    dispatch(partnerDetailsSliceAction.setReportFilterColumnAction(selectedColumns));

    setIsEditingTitle(false);
    setColumnSearch("");
  };

  const handleResetClick = () => {
    setReportTitle("");
    setIsEditingTitle(false);

    dispatch(partnerDetailsSliceAction.setReportFilterTitleAction(""));
    setGroupColumns([]);
    setGroupRows([]);
    setSelectedColumns([]);

    dispatch(partnerDetailsSliceAction.setReportFilterColumnAction([]));
    onResetFilters();
    setColumnSearch("");
  };

  const MenuProps = {
    PaperProps: { style: { maxHeight: 240, width: 250 } },
  };

  // Filter out already selected items so they do not appear in dropdown
  const getAvailableOptions = (all: string[], selected: string[]) =>
    all.filter((item) => !selected.includes(item));

  const availableGroupColumns = selectedColumns.filter(
    col => col !== groupRows[0]
  );

  const availableGroupRows = selectedColumns.filter(
    col => col !== groupColumns[0]
  );

  const renderSelectValue = (selected: string[], placeholder: string) => {
    if (selected.length === 0) return placeholder;
    return `${selected.length} selected`;
  };

  const filteredJoinedTablesFields = useMemo(() => {
    const col = columnSearch.trim().toLowerCase();
    if (!col) return joinedTablesFields;
    return joinedTablesFields.filter((item) => {
      const byLabel = (item.label || "").toLowerCase().includes(col);
      const byValue = (item.value || "").toLowerCase().includes(col);
      return byLabel || byValue;
    });
  }, [columnSearch, joinedTablesFields]);

  return (
    <div className="rd-filter-panel">

      {/* Report Title */}
      <div className="rd-filter-section" style={{ marginBottom: 20 }}>
        <strong>Report Title</strong>
        <TextField
          id="report-title-input"
          variant="outlined"
          size="small"
          placeholder="Untitled"
          value={reportTitle}
          onChange={(e) => {
            setIsEditingTitle(true);
            setReportTitle(e.target.value);
          }}
          fullWidth
        />
      </div>

      <div className="rd-filter-section" style={{ marginBottom: 10 }}>
        <InputLabel htmlFor="report-title-input" className="label-inline">
          Group
          {/* <span>0</span> */}
        </InputLabel>
        <strong>Group Column</strong>
        <FormControl fullWidth size="small" variant="outlined" disabled={selectedColumns.length === 0}>
          <Select
            labelId="group-column-label"
            value={groupColumns[0] || ""}
            onChange={(e) =>
              setGroupColumns(e.target.value ? [e.target.value] : [])
            }
            disabled={disabled}
            input={<OutlinedInput label="Add Group" />}
            displayEmpty
            renderValue={(selected) =>
              selected ? selected : <span>Select group column</span>
            }
          >
            <MenuItem value="">
              <span>Select group column</span>
            </MenuItem>

            {availableGroupColumns.map((name) => (
              <MenuItem key={name} value={name}>
                <ListItemText primary={getColumnLabel(name)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            marginTop: 1,
          }}
        >
        </Box>
      </div>

      <div className="rd-filter-section b-bottom" style={{ marginBottom: 15 }}>
        <strong>Group Row</strong>
        <FormControl fullWidth size="small" variant="outlined" disabled={selectedColumns.length === 0}>
          <Select
            labelId="group-row-label"
            value={groupRows[0] || ""}
            onChange={(e) =>
              setGroupRows(e.target.value ? [e.target.value] : [])
            }
            disabled={disabled}
            input={<OutlinedInput label="Add Group" />}
            displayEmpty
            renderValue={(selected) =>
              selected ? selected : <span>Select group row</span>
            }
          >

            <MenuItem value="">
              <span>Select group row</span>
            </MenuItem>

            {availableGroupRows.map((name) => (
              <MenuItem key={name} value={name}>
                <ListItemText primary={getColumnLabel(name)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            marginTop: 1,
          }}
        >
        </Box>
      </div>

      {/* Column Selection */}
      <div className="rd-filter-section" style={{ marginBottom: 10 }}>
        <InputLabel htmlFor="report-title-input" className="label-inline">
          Column <span>0</span>
        </InputLabel>
        {/* <strong>Column</strong> */}
        {joinedTablesFields.length > 0 &&
          <FormControl fullWidth size="small" variant="outlined">
            <Select
              labelId="columns-label"
              multiple
              value={selectedColumns}
              onChange={handleSelectChange(setSelectedColumns)}
              input={<OutlinedInput label="Add Column" />}
              renderValue={() => renderSelectValue(selectedColumns, "Add Column")}
              MenuProps={MenuProps}
              displayEmpty
            >
              <ListSubheader
                disableSticky
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{ px: 1, py: 0 }}
              >
                <SearchInput
                  theme="dark"
                  id="columnSearch"
                  name="columnSearch"
                  placeholder="Search columns…"
                  isDisplayClearIcon={true}
                  value={columnSearch}
                  onChange={(event: any) => {
                    event.stopPropagation?.();
                    setColumnSearch(event.currentTarget.value);
                  }}
                  onSearch={(_, value: string) => setColumnSearch(value)}
                  onKeyDown={(e: any) => e.stopPropagation?.()}
                />
              </ListSubheader>
              {filteredJoinedTablesFields.map((name: columnFilterLabelType) => (
                <MenuItem key={name.value} value={name.value}>
                  <ListItemText primary={name.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }

        {/* Quick Select Checkboxes */}
        <Box sx={{ display: "flex", flexDirection: "column", marginTop: 1 }}>
          {QUICK_SELECT_COLUMNS.map((col) => (
            <FormControl key={col.value} sx={{ marginBottom: 0.5, width: "100%" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <input
                  type="checkbox"
                  style={{ marginRight: 4, flexShrink: 0 }}
                  checked={selectedColumns.includes(col.value)}
                  onChange={(e) => {
                    let updatedColumns: string[];
                    if (e.target.checked) {
                      updatedColumns = [...selectedColumns, col.value];
                    } else {
                      updatedColumns = selectedColumns.filter((c) => c !== col.value);
                    }

                    setSelectedColumns(updatedColumns);
                    dispatch(partnerDetailsSliceAction.setReportFilterColumnAction(updatedColumns));
                  }}
                />
                <span
                  title={col.label}
                  style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                    flex: 1,
                    display: "block",
                  }}
                >
                  {col.label}
                </span>
              </label>
            </FormControl>
          ))}
        </Box>

        {/* Chips below dropdown */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            marginTop: 1,
          }}
        >

          {selectedColumns.map((value) => (
            <Chip
              key={value}
              label={getColumnLabel(value)}
              onDelete={() => handleRemoveChip(value, setSelectedColumns)}
            />
          ))}
        </Box>
      </div>

      {/* Buttons */}
      {!readOnly && (
        <div className="rd-filter-buttons">
          <Button
            variant="text"
            color="primary"
            onClick={handleResetClick}
            className="rd-filter-button-reset"
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyClick}
            className="rd-filter-button-apply"
            disabled={
              !reportTitle.trim() &&
              groupColumns.length === 0 &&
              groupRows.length === 0 &&
              selectedColumns.length === 0
            }
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
};

export default OutlineTab;